import React, { useState } from 'react';
import { Edit3, CheckCircle2, Shield, X, Save, CalendarDays, RotateCcw, Trash2, Clock, MessageCircle, Loader2 } from 'lucide-react';
import { doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { auth } from '../../lib/firebase';
import { BracketMatch, Tournament, Team, Config, Match } from '../../types';
import { SearchableTeamSelect } from '../common/SearchableTeamSelect';
import { StatsService } from '../../services/statsService';
import { RankingService } from '../../services/rankingService';

interface AdminMatchEditModalProps {
  m: BracketMatch;
  mIdx: number;
  bIdx: number;
  t: Tournament;
  allTeams: Team[];
  db: any;
  getCollectionPath: (col: string) => string;
  onClose: () => void;
  onPublish?: () => void;
  config: Config;
  allMatches?: any[];
}

export function AdminMatchEditModal({
  m,
  mIdx,
  bIdx,
  t,
  allTeams,
  db,
  getCollectionPath,
  onClose,
  onPublish,
  config,
  allMatches = [],
}: AdminMatchEditModalProps) {
  const [localM, setLocalM] = useState<BracketMatch>({ ...m });
  const [localError, setLocalError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const matchId = `bracket_${t.id}_${bIdx}_${mIdx}`;
  const isAlreadyInCollection = allMatches.some(gm => gm.id === matchId);

  // Solamente mostrar los equipos que participan en este torneo
  const getTournamentTeamIds = () => {
    const ids = new Set<string>();
    t.zones?.forEach(z => z.teams.forEach(tm => ids.add(tm.teamId)));
    t.brackets?.forEach(b => b.matches.forEach(m => {
      if (m.team1Id && m.team1Id !== 'BYE') ids.add(m.team1Id);
      if (m.team2Id && m.team2Id !== 'BYE') ids.add(m.team2Id);
    }));
    if (localM.team1Id) ids.add(localM.team1Id);
    if (localM.team2Id) ids.add(localM.team2Id);
    return ids;
  };
  const tournamentTeamIds = getTournamentTeamIds();
  const tournamentTeams = allTeams.filter(team => tournamentTeamIds.has(team.id));

  type SetId = 1 | 2 | 3;
  type TeamId = 1 | 2;
  const [activeBox, setActiveBox] = useState<{set: SetId, team: TeamId} | null>(null);

  const getWinnerOfSet = (setRaw: string) => {
    if (!setRaw) return null;
    const [t1score, t2score] = setRaw.split('-').map(Number);
    if (isNaN(t1score) || isNaN(t2score)) return null;
    if (t1score > t2score) return 1;
    if (t2score > t1score) return 2;
    return null;
  };

  const s1Winner = getWinnerOfSet(localM.s1 || '');
  const s2Winner = getWinnerOfSet(localM.s2 || '');
  const showSet3 = s1Winner && s2Winner && s1Winner !== s2Winner;
  
  // Clear s3 if it shouldn't be shown anymore
  React.useEffect(() => {
    if (s1Winner && s2Winner && s1Winner === s2Winner && localM.s3 !== '') {
      setLocalM((prev) => ({ ...prev, s3: '' }));
    }
  }, [s1Winner, s2Winner, localM.s3]);

  const handleNumberSelect = (num: number) => {
    if (!activeBox) return;
    const { set, team } = activeBox;
    
    // Update score
    const currentScoreStr = set === 1 ? localM.s1 : set === 2 ? localM.s2 : localM.s3;
    const currentParts = (currentScoreStr || '').split('-');
    const newT1 = team === 1 ? num.toString() : (currentParts[0] || '');
    const newT2 = team === 2 ? num.toString() : (currentParts[1] || '');
    const newScore = `${newT1}-${newT2}`;

    setLocalM(prev => ({
      ...prev,
      [set === 1 ? 's1' : set === 2 ? 's2' : 's3']: newScore
    }));

    // Auto-advance logic (requested sequence: A1 -> B1 -> A2 -> B2 -> A3 -> B3)
    const otherTeam: TeamId = team === 1 ? 2 : 1;
    const currentSetScore = set === 1 ? localM.s1 : set === 2 ? localM.s2 : localM.s3;
    const otherVal = getScore(currentSetScore, otherTeam);

    if (otherVal === '') {
      // If the opponent in the SAME set is empty, always go there first
      setActiveBox({ set, team: otherTeam });
    } else {
      // Both scores for the current set are filled.
      
      // Validation of Padel Set Congruency
      const t1Val = team === 1 ? num : parseInt(getScore(currentSetScore, 1) || '0');
      const t2Val = team === 2 ? num : parseInt(getScore(currentSetScore, 2) || '0');
      const setStr = `${t1Val}-${t2Val}`;
      const isInvalid = !isValidPadelSet(setStr);

      if (isInvalid) {
        setActiveBox({ set, team: otherTeam });
        return;
      }

      // If valid, move to next or close (Smart Edit Mode)
      if (set === 1) {
        const nextSetScore = getScore(localM.s2, otherTeam);
        if (nextSetScore === '') {
          setActiveBox({ set: 2, team: otherTeam });
        } else {
          setActiveBox(null);
        }
      } else if (set === 2) {
        const s1W = s1Winner;
        const s2W = t1Val > t2Val ? 1 : (t2Val > t1Val ? 2 : null);

        if (s1W && s2W && s1W !== s2W) {
          const s3NextScore = getScore(localM.s3, otherTeam);
          if (s3NextScore === '') {
            setActiveBox({ set: 3, team: otherTeam });
          } else {
            setActiveBox(null);
          }
        } else {
          setActiveBox(null);
        }
      } else {
        setActiveBox(null); // Finished set 3
      }
    }
  };

  const isValidPadelSet = (str: string) => {
    if (!str) return false;
    const [t1score, t2score] = str.split('-').map(Number);
    if (isNaN(t1score) || isNaN(t2score)) return false;
    if (t1score === 6 && t2score <= 4) return true;
    if (t2score === 6 && t1score <= 4) return true;
    if (t1score === 7 && (t2score === 5 || t2score === 6)) return true;
    if (t2score === 7 && (t1score === 5 || t1score === 6)) return true;
    return false;
  };

  const handleSave = async () => {
    const hasScore = localM.s1 || localM.s2 || localM.s3;
    
    let calculatedWinnerId = localM.winnerId;

    if (hasScore) {
      if (!isValidPadelSet(localM.s1 || '')) {
        setLocalError('⚠️ El Set 1 tiene un resultado inválido para Padel.');
        setActiveBox({ set: 1, team: 1 });
        return;
      }
      if (!isValidPadelSet(localM.s2 || '')) {
        setLocalError('⚠️ El Set 2 tiene un resultado inválido para Padel.');
        setActiveBox({ set: 2, team: 1 });
        return;
      }
  
      if (showSet3) {
        if (!localM.s3 || !isValidPadelSet(localM.s3)) {
          setLocalError('⚠️ Debes completar el Set 3 correctamente (están 1 a 1).');
          setActiveBox({ set: 3, team: 1 });
          return;
        }
      }
  
      // Determine winner based on sets
      const t1Wins = (s1Winner === 1 ? 1 : 0) + (s2Winner === 1 ? 1 : 0) + (getWinnerOfSet(localM.s3 || '') === 1 ? 1 : 0);
      const t2Wins = (s1Winner === 2 ? 1 : 0) + (s2Winner === 2 ? 1 : 0) + (getWinnerOfSet(localM.s3 || '') === 2 ? 1 : 0);
      calculatedWinnerId = t1Wins > t2Wins ? localM.team1Id : localM.team2Id;
    }

    const t1Obj = allTeams.find(t => t.id === localM.team1Id);
    const t2Obj = allTeams.find(t => t.id === localM.team2Id);
    let pIds: string[] = [];
    if (t1Obj) pIds.push(t1Obj.player1Id, t1Obj.player2Id, t1Obj.player3Id || '');
    if (t2Obj) pIds.push(t2Obj.player1Id, t2Obj.player2Id, t2Obj.player3Id || '');
    pIds = pIds.filter(Boolean);

    let winnerPlayerIds: string[] = [];
    if (calculatedWinnerId === localM.team1Id && t1Obj) {
      winnerPlayerIds = [t1Obj.player1Id, t1Obj.player2Id, t1Obj.player3Id || ''].filter(Boolean);
    } else if (calculatedWinnerId === localM.team2Id && t2Obj) {
      winnerPlayerIds = [t2Obj.player1Id, t2Obj.player2Id, t2Obj.player3Id || ''].filter(Boolean);
    }

    setIsSaving(true);
    setLocalError('');
    setActiveBox(null);

    try {
      const hasResultToSave = !!(calculatedWinnerId || localM.score || localM.s1 || localM.s2 || localM.s3);
      const isExpressAutoPublish = t.managementMode === 'express' && !!localM.team1Id && !!localM.team2Id && localM.team1Id !== 'BYE' && localM.team2Id !== 'BYE';
      const isPublishedNow = isAlreadyInCollection || m.isPublished || hasResultToSave || isExpressAutoPublish;
      const updatedMatch = { ...localM, winnerId: calculatedWinnerId, isPublished: isPublishedNow };
      
      if (bIdx === 999) {
        // Update Tournament 3rd place match
        await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), { thirdPlaceMatch: updatedMatch });
      } else {
        const nb = JSON.parse(JSON.stringify(t.brackets));
        nb[bIdx].matches[mIdx] = updatedMatch;

        // Auto-Progression
        const nextBIdx = bIdx + 1;
        const nextMIdx = Math.floor(mIdx / 2);
        const isTeam1Slot = mIdx % 2 === 0;

        if (nb[nextBIdx] && nb[nextBIdx].matches[nextMIdx]) {
          const nextMatch = nb[nextBIdx].matches[nextMIdx];
          if (m.winnerId && m.winnerId !== calculatedWinnerId) {
            if (isTeam1Slot && nextMatch.team1Id === m.winnerId) nextMatch.team1Id = '';
            if (!isTeam1Slot && nextMatch.team2Id === m.winnerId) nextMatch.team2Id = '';
          }
          if (calculatedWinnerId) {
            if (isTeam1Slot) nextMatch.team1Id = calculatedWinnerId;
            else nextMatch.team2Id = calculatedWinnerId;
          }

          const nextMatchId = `bracket_${t.id}_${nextBIdx}_${nextMIdx}`;
          const isNextPublished = allMatches.some(gm => gm.id === nextMatchId) || nextMatch.isPublished;
          
          if (!isNextPublished && nextMatch.team1Id && nextMatch.team2Id && nextMatch.team1Id !== 'BYE' && nextMatch.team2Id !== 'BYE') {
            nextMatch.isPublished = true;
            const t1NextObj = allTeams.find(t => t.id === nextMatch.team1Id) || null;
            const t2NextObj = allTeams.find(t => t.id === nextMatch.team2Id) || null;
            let nextPIds: string[] = [];
            if (t1NextObj) nextPIds.push(t1NextObj.player1Id, t1NextObj.player2Id, t1NextObj.player3Id || '');
            if (t2NextObj) nextPIds.push(t2NextObj.player1Id, t2NextObj.player2Id, t2NextObj.player3Id || '');
            nextPIds = nextPIds.filter(Boolean);

            await setDoc(doc(db, getCollectionPath('matches'), nextMatchId), {
              tournamentId: t.id,
              tournamentName: t.name,
              round: t.brackets[nextBIdx]?.round || 'Fase Final',
              team1Id: nextMatch.team1Id,
              team2Id: nextMatch.team2Id,
              s1: '', s2: '', s3: '', score: '',
              date: nextMatch.date || '',
              time: nextMatch.time || '',
              winnerId: null,
              organizerId: t.organizerId || auth.currentUser?.uid || '',
              status: 'pending',
              createdAt: Date.now(),
              participantIds: nextPIds,
              winnerPlayerIds: [],
              team1Obj: t1NextObj,
              team2Obj: t2NextObj
            });
          } else if (isNextPublished) {
            await updateDoc(doc(db, getCollectionPath('matches'), nextMatchId), {
              team1Id: nextMatch.team1Id || '',
              team2Id: nextMatch.team2Id || '',
              status: nextMatch.winnerId ? 'completed' : 'pending',
              team1Obj: allTeams.find(t => t.id === nextMatch.team1Id) || null,
              team2Obj: allTeams.find(t => t.id === nextMatch.team2Id) || null
            });
          }
        }
        
        // Third Place Match Logic
        let updatedThirdPlaceMatch = t.thirdPlaceMatch;
        if (nextBIdx === nb.length - 1) {
          const loserId = calculatedWinnerId ? (calculatedWinnerId === nb[bIdx].matches[mIdx].team1Id ? nb[bIdx].matches[mIdx].team2Id : nb[bIdx].matches[mIdx].team1Id) : '';
          
          if (!updatedThirdPlaceMatch) {
            updatedThirdPlaceMatch = {
              id: `bracket_${t.id}_999_0`,
              team1Id: '',
              team2Id: '',
              winnerId: '',
              s1: '', s2: '', s3: '', score: '',
              isPublished: false
            };
          }

          if (mIdx === 0) {
            updatedThirdPlaceMatch.team1Id = loserId || '';
          } else if (mIdx === 1) {
            updatedThirdPlaceMatch.team2Id = loserId || '';
          }
          
          const thirdPlaceMatchId = `bracket_${t.id}_999_0`;
          let isThirdPublished = updatedThirdPlaceMatch.isPublished;
          
          if (!isThirdPublished && updatedThirdPlaceMatch.team1Id && updatedThirdPlaceMatch.team2Id && updatedThirdPlaceMatch.team1Id !== 'BYE' && updatedThirdPlaceMatch.team2Id !== 'BYE') {
             updatedThirdPlaceMatch.isPublished = true;
             
             const t1ThirdObj = allTeams.find(tm => tm.id === updatedThirdPlaceMatch?.team1Id) || null;
             const t2ThirdObj = allTeams.find(tm => tm.id === updatedThirdPlaceMatch?.team2Id) || null;
             let thirdPIds: string[] = [];
             if (t1ThirdObj) thirdPIds.push(t1ThirdObj.player1Id, t1ThirdObj.player2Id, t1ThirdObj.player3Id || '');
             if (t2ThirdObj) thirdPIds.push(t2ThirdObj.player1Id, t2ThirdObj.player2Id, t2ThirdObj.player3Id || '');
             thirdPIds = thirdPIds.filter(Boolean);
             
             await setDoc(doc(db, getCollectionPath('matches'), thirdPlaceMatchId), {
               tournamentId: t.id,
               tournamentName: t.name || '',
               round: '3er Puesto',
               team1Id: updatedThirdPlaceMatch.team1Id,
               team2Id: updatedThirdPlaceMatch.team2Id,
               s1: '', s2: '', s3: '', score: '',
               date: updatedThirdPlaceMatch.date || '',
               time: updatedThirdPlaceMatch.time || '',
               status: 'pending',
               organizerId: t.organizerId || auth.currentUser?.uid || '',
               createdAt: Date.now(),
               participantIds: thirdPIds,
               winnerPlayerIds: [],
               team1Obj: t1ThirdObj,
               team2Obj: t2ThirdObj
             }, { merge: true });
          } else if (isThirdPublished) {
             await setDoc(doc(db, getCollectionPath('matches'), thirdPlaceMatchId), {
               team1Id: updatedThirdPlaceMatch.team1Id || '',
               team2Id: updatedThirdPlaceMatch.team2Id || '',
               status: 'pending',
               team1Obj: allTeams.find(tm => tm.id === updatedThirdPlaceMatch?.team1Id) || null,
               team2Obj: allTeams.find(tm => tm.id === updatedThirdPlaceMatch?.team2Id) || null
             }, { merge: true });
          }
        }

        // Update Tournament brackets
        const updatePayload: any = { brackets: nb };
        if (nextBIdx === nb.length - 1 && updatedThirdPlaceMatch) {
          updatePayload.thirdPlaceMatch = updatedThirdPlaceMatch;
        }
        await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), updatePayload);
      }

      // Handle Global Matches Collection (Public Grid)
      if (isPublishedNow) {
        const oldMatch = allMatches.find(gm => gm.id === matchId);
        
        let pIdsToRevert: string[] = [];
        // REVERT LOGIC: If it was completed, and we are clearing it or changing winner, revert points and stats
        if (oldMatch && oldMatch.status === 'completed') {
          if (!calculatedWinnerId || !localM.team1Id || !localM.team2Id || calculatedWinnerId !== oldMatch.winnerId) {
            pIdsToRevert = oldMatch.participantIds || [];
            if (pIdsToRevert.length > 0) {
              await RankingService.revertMatchPoints(matchId, pIdsToRevert);
            }
          }
        }

        if (localM.team1Id && localM.team2Id) {
          const publicPayload: any = {
            tournamentId: t.id,
            tournamentName: t.name,
            round: bIdx === 999 ? '3er Puesto' : (t.brackets[bIdx]?.round || 'Fase Final'),
            team1Id: localM.team1Id,
            team2Id: localM.team2Id,
            s1: localM.s1 || '',
            s2: localM.s2 || '',
            s3: localM.s3 || '',
            score: localM.score || '',
            date: localM.date || '',
            time: localM.time || '',
            winnerId: calculatedWinnerId || null,
            organizerId: t.organizerId || auth.currentUser?.uid || '',
            status: calculatedWinnerId ? 'completed' : 'pending',
            createdAt: Date.now(),
            participantIds: pIds,
            winnerPlayerIds,
            team1Obj: t1Obj || null,
            team2Obj: t2Obj || null
          };
          
          if (oldMatch && oldMatch.pointsProcessed) {
            publicPayload.pointsProcessed = true;
          }
          if (oldMatch && oldMatch.status === 'completed' && !calculatedWinnerId) {
             publicPayload.pointsProcessed = false;
          }

          await setDoc(doc(db, getCollectionPath('matches'), matchId), publicPayload, { merge: true });
          
          if (calculatedWinnerId && (!oldMatch || oldMatch.status !== 'completed' || calculatedWinnerId !== oldMatch.winnerId)) {
            StatsService.processMatchStats({ id: matchId, ...publicPayload } as Match).catch(e => console.error("Error updating stats", e));
            RankingService.processMatchPoints({ id: matchId, ...publicPayload } as Match).catch(e => console.error("Error updating points", e));
          }
        } else {
          await deleteDoc(doc(db, getCollectionPath('matches'), matchId)).catch(() => {});
        }

        // Recalculate stats AFTER updating or deleting the match document
        if (pIdsToRevert.length > 0) {
          for (const pid of pIdsToRevert) {
            await StatsService.recalculatePlayerStats(pid).catch(e => console.error("Error recalculating stats", e));
          }
        }
      }

      onClose();
    } catch (err) {
      console.error(err);
      setLocalError('Ocurrió un error al guardar los cambios.');
    } finally {
      setIsSaving(false);
    }
  };

  const getScore = (setRaw: string | undefined, team: 1 | 2) => {
    if (!setRaw) return '';
    const parts = setRaw.split('-');
    return team === 1 ? parts[0] || '' : parts[1] || '';
  };

  const setScore = (field: 's1' | 's2' | 's3', team: 1 | 2, value: string) => {
    const raw = value.replace(/[^0-9]/g, '').slice(0, 2);
    const current = localM[field] || '';
    const currentParts = current.split('-');
    const t1 = team === 1 ? raw : (currentParts[0] || '');
    const t2 = team === 2 ? raw : (currentParts[1] || '');
    
    let combined = '';
    if (t1 || t2) combined = `${t1}-${t2}`;
    setLocalM({ ...localM, [field]: combined });
  };

  const handleClearResult = () => {
    setLocalM(prev => ({
      ...prev,
      s1: '',
      s2: '',
      s3: '',
      score: '',
      winnerId: ''
    }));
    setActiveBox(null);
  };

  const handleDeleteTeams = async () => {
    if (!await window.confirm('¿Estás seguro de querer eliminar las parejas y vaciar el casillero? Se revertirán todos los puntos y estadísticas relacionados a este partido.')) return;
    
    setIsDeleting(true);
    setLocalError('');
    setActiveBox(null);

    try {
      // 1. Revert global match if it exists and was completed
      if (isAlreadyInCollection) {
        const oldMatch = allMatches.find(gm => gm.id === matchId);
        if (oldMatch && oldMatch.status === 'completed') {
           const pIdsToRevert = oldMatch.participantIds || [];
           if (pIdsToRevert.length > 0) {
             await RankingService.revertMatchPoints(matchId, pIdsToRevert);
             await deleteDoc(doc(db, getCollectionPath('matches'), matchId)).catch(() => {});
             for (const pid of pIdsToRevert) {
               await StatsService.recalculatePlayerStats(pid);
             }
           } else {
             await deleteDoc(doc(db, getCollectionPath('matches'), matchId)).catch(() => {});
           }
        } else {
          await deleteDoc(doc(db, getCollectionPath('matches'), matchId)).catch(() => {});
        }
      }

      // 2. Clear from tournament brackets
      const emptyMatch = { ...m, team1Id: '', team2Id: '', t1: '', t2: '', s1: '', s2: '', s3: '', score: '', winnerId: '', isPublished: false };
      
      if (bIdx === 999) {
        await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), { thirdPlaceMatch: emptyMatch });
      } else {
        const nb = JSON.parse(JSON.stringify(t.brackets));
        nb[bIdx].matches[mIdx] = emptyMatch;

        // Clear downstream winner
        const nextBIdx = bIdx + 1;
        const nextMIdx = Math.floor(mIdx / 2);
        const isTeam1Slot = mIdx % 2 === 0;

        if (nb[nextBIdx] && nb[nextBIdx].matches[nextMIdx]) {
          const nextMatch = nb[nextBIdx].matches[nextMIdx];
          if (m.winnerId) {
            if (isTeam1Slot && nextMatch.team1Id === m.winnerId) { nextMatch.team1Id = ''; nextMatch.t1 = ''; }
            if (!isTeam1Slot && nextMatch.team2Id === m.winnerId) { nextMatch.team2Id = ''; nextMatch.t2 = ''; }
            nextMatch.winnerId = ''; nextMatch.score = ''; nextMatch.s1 = ''; nextMatch.s2 = ''; nextMatch.s3 = ''; nextMatch.isPublished = false;
            await deleteDoc(doc(db, getCollectionPath('matches'), `bracket_${t.id}_${nextBIdx}_${nextMIdx}`)).catch(() => {});
          }
        }
        
        await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), { brackets: nb });
      }

      onClose();
    } catch (err) {
      console.error(err);
      setLocalError('Ocurrió un error al vaciar el casillero.');
      setIsDeleting(false);
    }
  };


  const renderBox = (setNum: SetId, teamNum: TeamId) => {
    const isVisible = setNum !== 3 || showSet3 || (localM.s3 !== '');
    if (!isVisible) return <div className="w-7 h-7 md:w-9 md:h-9 border border-dashed border-white/15 rounded-lg opacity-35 bg-white/5 pointer-events-none transition-all duration-300" />;

    const isActive = activeBox?.set === setNum && activeBox?.team === teamNum;
    const val = getScore(setNum === 1 ? localM.s1 : setNum === 2 ? localM.s2 : localM.s3, teamNum);

    return (
      <div
        onClick={() => {
          if (activeBox?.set === setNum && activeBox?.team === teamNum) {
            setActiveBox(null);
          } else {
            setActiveBox({ set: setNum, team: teamNum });
          }
        }}
        className={`w-7 h-7 md:w-9 md:h-9 bg-white/5 rounded-lg font-black text-center flex items-center justify-center transition-all text-xs md:text-sm relative z-[150] ${
          isActive
            ? 'border-2 theme-primary-border bg-theme-primary/20 shadow-lg scale-110 cursor-pointer'
            : 'border border-white/10 hover:border-white/30 cursor-pointer'
        }`}
      >
        <span className={val !== '' ? 'text-white' : 'text-white/20'}>{val !== '' ? val : '-'}</span>
      </div>
    );
  };

  const roundName = bIdx === 999 ? '3er Puesto' : (t.brackets[bIdx]?.round || 'Fase Final');

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-2 sm:p-4 overflow-hidden">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm animate-in fade-in duration-300" onClick={onClose}></div>
      
      <div className="w-full max-w-md bg-[#1e293b] rounded-[2rem] shadow-[0_0_50px_rgba(0,0,0,0.5)] relative z-10 flex flex-col max-h-[95vh] animate-in zoom-in-95 duration-300">
        
        {/* Modal Header */}
        <div className="flex-none p-5 md:p-6 flex items-center justify-between bg-[#1e293b] z-20 rounded-t-3xl">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl theme-primary-bg flex items-center justify-center text-black shadow-lg shadow-theme-primary/20">
              <Edit3 size={24} />
            </div>
            <div>
              <h3 className="text-lg font-black uppercase tracking-widest text-white leading-none">Editar Partido</h3>
              <p className="text-[9px] theme-primary-text font-black uppercase tracking-wider mt-1 opacity-60 italic">{roundName}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors">
            <X size={16} className="opacity-40" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 p-4 md:p-5 space-y-4 overflow-y-auto custom-scrollbar" style={{ paddingBottom: '20px' }}>
          
          {localError && (
            <div className="bg-red-500/10 border border-red-500/20 p-3 rounded-xl flex items-center gap-2 animate-in shake duration-500">
              <span className="text-xs font-bold text-red-400">{localError}</span>
            </div>
          )}

          {/* Teams Selection */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 opacity-40 px-2">
              <CalendarDays size={14} />
              <span className="text-[10px] font-black uppercase tracking-[0.2em]">Enfrentamiento</span>
              <div className="h-px flex-1 bg-white/10" />
            </div>

            <div className="grid gap-3">
              {/* Equipo 1 */}
              <div className={`p-4 rounded-2xl border transition-all ${localM.winnerId === localM.team1Id && localM.team1Id ? 'theme-primary-border bg-theme-primary/5' : 'bg-black/20 border-white/5'}`} style={{ zIndex: 30, position: 'relative' }}>
                <div className="flex items-center gap-4">
                  <div className="flex-1 min-w-0" style={{ overflow: 'visible' }}>
                     <SearchableTeamSelect
                      value={localM.team1Id || ''}
                      options={tournamentTeams}
                      onChange={(val) => setLocalM({ ...localM, team1Id: val })}
                      placeholder="Buscar Pareja 1..."
                      className="w-full bg-transparent font-black tracking-tight text-white outline-none text-xs sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* VS Divider */}
              <div className="flex items-center justify-center gap-4 py-1">
                <div className="h-px flex-1 bg-white/5" />
                <span className="text-xs font-black italic opacity-20 tracking-widest">VS</span>
                <div className="h-px flex-1 bg-white/5" />
              </div>

              {/* Pareja 2 */}
              <div className={`p-4 rounded-2xl border transition-all ${localM.winnerId === localM.team2Id && localM.team2Id ? 'theme-primary-border bg-theme-primary/5' : 'bg-black/20 border-white/5'}`} style={{ zIndex: 20, position: 'relative' }}>
                <div className="flex items-center gap-4">
                  <div className="flex-1 min-w-0" style={{ overflow: 'visible' }}>
                     <SearchableTeamSelect
                      value={localM.team2Id || ''}
                      options={tournamentTeams}
                      onChange={(val) => setLocalM({ ...localM, team2Id: val })}
                      placeholder="Buscar Pareja 2..."
                      className="w-full bg-transparent font-black tracking-tight text-white outline-none text-xs sm:text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Marcador Profesional Matrix */}
          <div className="space-y-4 relative mt-6">
            <div className="flex items-center gap-2 opacity-40 px-2">
              <Shield size={14} />
              <span className="text-[10px] font-black uppercase tracking-[0.2em]">Resultados</span>
              <div className="h-px flex-1 bg-white/10" />
            </div>

            <div className="bg-black/40 p-1.5 md:p-2 rounded-xl border border-white/5 mt-1 w-full flex flex-col gap-1 relative shadow-inner">
              <div className="flex items-center gap-3 px-1 mb-0.5 justify-start pr-[58px] md:pr-[68px]">
                <span className="flex-1 min-w-0 text-[8px] font-bold opacity-30 uppercase flex items-center gap-1 truncate">
                  <div className="w-1 h-1 rounded-full theme-primary-bg opacity-50" />
                  Score
                </span>
                <div className="flex gap-1.5 shrink-0">
                  <span className="w-7 md:w-9 text-center text-[7px] font-black opacity-20">S1</span>
                  <span className="w-7 md:w-9 text-center text-[7px] font-black opacity-20">S2</span>
                  <span className="w-7 md:w-9 text-center text-[7px] font-black opacity-20 transition-opacity" style={{ opacity: showSet3 ? 0.2 : 0.08 }}>S3</span>
                </div>
              </div>

              {/* Pareja 1 Score Row */}
              <div className="flex items-center justify-start relative gap-3 p-1 rounded-lg transition-all duration-300 pr-[58px] md:pr-[68px]">
                <div className={`flex-1 min-w-0 transition-all duration-300 text-[8px] md:text-[9.5px] font-black uppercase leading-none truncate ${localM.winnerId === localM.team1Id ? 'opacity-100 theme-primary-text' : 'opacity-60'}`} title={allTeams.find(t => t.id === localM.team1Id)?.name || 'Pareja 1'}>
                  {allTeams.find(t => t.id === localM.team1Id)?.name || 'Pareja 1'}
                </div>
                <div className="flex gap-1.5 shrink-0">
                  {renderBox(1, 1)}
                  {renderBox(2, 1)}
                  {renderBox(3, 1)}
                </div>
              </div>

              {/* Pareja 2 Score Row */}
              <div className="flex items-center justify-start relative gap-3 p-1 rounded-lg transition-all duration-300 pr-[58px] md:pr-[68px]">
                <div className={`flex-1 min-w-0 transition-all duration-300 text-[8px] md:text-[9.5px] font-black uppercase leading-none truncate ${localM.winnerId === localM.team2Id ? 'opacity-100 theme-primary-text' : 'opacity-60'}`} title={allTeams.find(t => t.id === localM.team2Id)?.name || 'Pareja 2'}>
                  {allTeams.find(t => t.id === localM.team2Id)?.name || 'Pareja 2'}
                </div>
                <div className="flex gap-1.5 shrink-0">
                  {renderBox(1, 2)}
                  {renderBox(2, 2)}
                  {renderBox(3, 2)}
                </div>
              </div>

              {/* Modificar Button Placeholder to match layout */}
              <div className="absolute right-1 bottom-1.5 md:bottom-2 flex flex-col gap-1 z-10 w-[52px] md:w-[62px]">
                {activeBox === null && (
                  <button
                    onClick={() => setActiveBox({ set: 1, team: 1 })}
                    className="h-[20px] md:h-[22px] w-full bg-yellow-400 hover:bg-yellow-300 text-black font-black px-1 rounded-md transition-all flex items-center justify-center border border-yellow-300 shadow active:scale-95 cursor-pointer"
                  >
                    <span className="text-[7.5px] md:text-[8px] uppercase tracking-widest leading-none">Modificar</span>
                  </button>
                )}
                {/* No Guardar Button here, because we have a global "GUARDAR" at the bottom of the modal */}
                <div className="h-[40px] md:h-[46px] w-full opacity-0 pointer-events-none" />
              </div>

              {/* Subtle Number Picker */}
              {activeBox && (
                <>
                  <div 
                    className="fixed inset-0 z-[140] cursor-default"
                    onClick={() => setActiveBox(null)}
                  />
                  <div className="mt-2 pt-2 border-t border-white/5 relative z-[150] animate-in slide-in-from-top-2 fade-in duration-200 bg-black/50 -mx-1.5 -mb-1.5 p-2 rounded-b-xl backdrop-blur-md">
                    <div className="flex justify-center gap-0.5 sm:gap-1.5">
                      {[0, 1, 2, 3, 4, 5, 6, 7].map((n) => (
                        <button
                          key={n}
                          onClick={() => handleNumberSelect(n)}
                          className="flex-1 max-w-[32px] h-8 md:max-w-[40px] md:h-10 bg-white/5 hover:bg-theme-primary/20 hover:scale-110 rounded-lg font-black text-xs md:text-sm transition-all duration-200 active:scale-95 border border-white/10 hover:theme-primary-border text-white shadow-lg"
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

        </div>

        {/* Modal Footer */}
        <div className="flex-none p-4 md:p-5 bg-black/20 flex items-center justify-between gap-2 rounded-b-3xl">
          <div className="flex-1">
            {(localM.s1 || localM.s2 || localM.s3 || localM.score || localM.winnerId) ? (
              <button
                onClick={handleClearResult}
                disabled={isSaving || isDeleting}
                className="flex items-center gap-1.5 text-white hover:text-white/80 font-black text-[8px] md:text-[9px] uppercase tracking-wider transition-colors bg-white/5 hover:bg-white/10 px-2.5 py-1.5 rounded-lg border border-white/10"
              >
                <RotateCcw size={10} /> Limpiar Resultado
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={isSaving || isDeleting}
              className="px-4 py-2 text-[10px] font-black uppercase tracking-widest opacity-40 hover:opacity-100 transition-opacity"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || isDeleting}
              className="flex items-center gap-2 theme-primary-bg text-black font-black px-6 py-2.5 rounded-xl text-[10px] uppercase tracking-[0.1em] hover:opacity-80 transition-all active:scale-95 shadow-lg disabled:opacity-50"
            >
              {isSaving ? '...' : <><Save size={14} /> Guardar</>}
            </button>
            {(localM.team1Id || localM.team2Id) && (
              <button
                onClick={handleDeleteTeams}
                disabled={isSaving || isDeleting}
                className={`flex items-center justify-center h-9 md:h-[38px] rounded-xl bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white transition-all border border-red-500/20 shadow-lg ml-1 shrink-0 ${isDeleting ? 'w-auto px-3' : 'w-9 md:w-[38px]'}`}
                title="Eliminar parejas y vaciar casillero"
              >
                {isDeleting ? (
                  <span className="flex items-center gap-1.5 font-black uppercase tracking-widest text-[9px] md:text-[10px]">
                    <Loader2 size={12} className="animate-spin" />
                    Eliminando...
                  </span>
                ) : (
                  <Trash2 size={16} />
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
